import {
	DeleteOutlined,
	EditOutlined,
	FileTextOutlined,
	CloudUploadOutlined,
} from '@ant-design/icons'
import {
	Button,
	Input,
	Popconfirm,
	Select,
	Space,
	Table,
	Tag,
	message,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import { ROUTE } from '../../utils/constant'
import './BlogList.scss'

const STATUS_COLORS = {
	draft: 'default',
	in_review: 'processing',
	published: 'success',
	archived: 'warning',
}

export const BlogList = () => {
	const history = useHistory()
	const [items, setItems] = useState([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [page, setPage] = useState(0)
	const [pageSize, setPageSize] = useState(20)
	const [status, setStatus] = useState(undefined)
	const [locale, setLocale] = useState(undefined)
	const [cluster, setCluster] = useState(undefined)
	const [query, setQuery] = useState('')

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const rs = await api.blogAdminApi.getPosts({
				page,
				pageSize,
				status,
				locale,
				cluster,
				q: query.trim() || undefined,
			})
			setItems(Array.isArray(rs?.data) ? rs.data : Array.isArray(rs?.items) ? rs.items : [])
			setTotal(Number(rs?.total) || 0)
		} catch (err) {
			message.error(err?.message || 'Could not load blog posts.')
		} finally {
			setLoading(false)
		}
	}, [page, pageSize, status, locale, cluster, query])

	useEffect(() => {
		load()
	}, [load])

	const handleDelete = async pk => {
		try {
			await api.blogAdminApi.deletePost(pk)
			message.success('Post deleted.')
			load()
		} catch (err) {
			message.error(err?.message || 'Could not delete post.')
		}
	}

	const handlePublish = async pk => {
		try {
			await api.blogAdminApi.publishPost(pk)
			message.success('Post published.')
			load()
		} catch (err) {
			message.error(err?.message || 'Could not publish post.')
		}
	}

	const columns = [
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
			ellipsis: true,
			render: (value, record) => value || record.id || '—',
		},
		{
			title: 'Slug',
			dataIndex: 'slug',
			key: 'slug',
			ellipsis: true,
		},
		{
			title: 'Intent',
			dataIndex: 'intentId',
			key: 'intentId',
			ellipsis: true,
			width: 160,
			render: value => value || '—',
		},
		{
			title: 'Locale',
			dataIndex: 'locale',
			key: 'locale',
			width: 90,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 120,
			render: value => (
				<Tag color={STATUS_COLORS[value] || 'default'}>{value}</Tag>
			),
		},
		{
			title: 'Cluster',
			dataIndex: 'cluster',
			key: 'cluster',
			ellipsis: true,
			render: value => value || '—',
		},
		{
			title: 'Published',
			dataIndex: 'publishedAt',
			key: 'publishedAt',
			width: 170,
			render: value => (value ? new Date(value).toLocaleString() : '—'),
		},
		{
			title: 'Updated',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 170,
			render: value => (value ? new Date(value).toLocaleString() : '—'),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 240,
			render: (_, record) => (
				<Space size={4}>
					<Button
						size="small"
						icon={<EditOutlined />}
						onClick={() =>
							history.push(
								`${ROUTE.BLOG_MANAGEMENT}${ROUTE.CREATE}?id=${record.pk}`
							)
						}
					>
						Edit
					</Button>
					<Button
						size="small"
						icon={<CloudUploadOutlined />}
						disabled={record.status === 'published'}
						onClick={() => handlePublish(record.pk)}
					>
						Publish
					</Button>
					<Popconfirm
						title="Delete this post? (draft/archived only)"
						onConfirm={() => handleDelete(record.pk)}
					>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	]

	return (
		<ListPageShell className="blog-list">
			<ListPageIntro
				icon={FileTextOutlined}
				title="Blog posts"
				subtitle="Landing /blog content. Same content id can have one row per locale."
			/>
			<div className="blog-list__toolbar">
				<Space wrap>
					<Input.Search
						allowClear
						placeholder="Search id, slug, title"
						onSearch={value => {
							setPage(0)
							setQuery(value || '')
						}}
						style={{ width: 260 }}
					/>
					<Select
						allowClear
						placeholder="Status"
						style={{ width: 140 }}
						value={status}
						onChange={value => {
							setPage(0)
							setStatus(value)
						}}
						options={[
							{ value: 'draft', label: 'Draft' },
							{ value: 'in_review', label: 'In review' },
							{ value: 'published', label: 'Published' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>
					<Select
						allowClear
						placeholder="Locale"
						style={{ width: 120 }}
						value={locale}
						onChange={value => {
							setPage(0)
							setLocale(value)
						}}
						options={[
							{ value: 'en', label: 'EN' },
							{ value: 'vi', label: 'VI' },
							{ value: 'ja', label: 'JA' },
							{ value: 'ko', label: 'KO' },
							{ value: 'zh', label: 'ZH' },
							{ value: 'es', label: 'ES' },
							{ value: 'fr', label: 'FR' },
						]}
					/>
					<Select
						allowClear
						placeholder="Cluster"
						style={{ width: 160 }}
						value={cluster}
						onChange={value => {
							setPage(0)
							setCluster(value)
						}}
						options={[
							{ value: 'vocab-tools', label: 'vocab-tools' },
							{ value: 'save-words', label: 'save-words' },
							{ value: 'exam-prep', label: 'exam-prep' },
							{ value: 'brand-proof', label: 'brand-proof' },
						]}
					/>
					<Button
						type="primary"
						onClick={() =>
							history.push(`${ROUTE.BLOG_MANAGEMENT}${ROUTE.CREATE}`)
						}
					>
						Create post
					</Button>
				</Space>
			</div>
			<Table
				rowKey="pk"
				loading={loading}
				columns={columns}
				dataSource={items}
				pagination={{
					current: page + 1,
					pageSize,
					total,
					showSizeChanger: true,
					onChange: (nextPage, nextSize) => {
						setPage(nextPage - 1)
						setPageSize(nextSize)
					},
				}}
			/>
		</ListPageShell>
	)
}
